/**
 * HostedBrandsSection (Iter 9.47.p)
 *
 * Renders the user's hosted_brands as status cards inside the /me Hosting
 * section. One card per brand × category combination. Status surfaces:
 *
 *   waitlist_status   joined         → "Waiting for {category} to launch in {city}"
 *                     invited        → "Subscriptions opening - finish your intake"
 *                     declined       → (hidden)
 *                     removed        → (hidden by API)
 *   intake_status     in_progress    → "Continue your intake"
 *                     submitted      → "Intake received - admin is preparing your dashboard"
 *   dashboard_status  awaiting_admin → "Dashboard pending activation"
 *                     live           → opens the live host dashboard link
 *                     paused         → "Paused - contact support to reactivate"
 *
 * Each card has a kebab → Edit / Remove popup (`HostedBrandEditModal`).
 */
import { useEffect, useState, useCallback } from "react";
import { Loader2, MoreVertical, MapPin, Tag, Hourglass, ArrowRight, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import axios from "axios";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COPY = {
  waitlist_joined:    (b) => `Waiting for ${(b.categories || []).join(" + ") || "your category"} to launch in ${b.city || "your city"}`,
  waitlist_invited:   () => "Subscriptions opening - finish your intake to secure your spot",
  intake_in_progress: () => "Continue your intake - your dashboard setup is in progress",
  intake_submitted:   () => "Intake received - admin is preparing your dashboard",
  dashboard_awaiting: () => "Dashboard pending activation by admin",
  dashboard_live:     () => "Your dashboard is live",
  dashboard_paused:   () => "Paused - contact support to reactivate",
};

function resolveStatusKey(brand) {
  if (brand.dashboard_status === "live") return "dashboard_live";
  if (brand.dashboard_status === "paused") return "dashboard_paused";
  if (brand.dashboard_status === "awaiting_admin") return "dashboard_awaiting";
  if (brand.intake_status === "submitted") return "intake_submitted";
  if (brand.intake_status === "in_progress") return "intake_in_progress";
  if (brand.waitlist_status === "invited") return "waitlist_invited";
  return "waitlist_joined";
}

function StatusBadge({ statusKey }) {
  const tone =
    statusKey.startsWith("dashboard_live") ? "bg-green-100 text-green-700"
    : statusKey.startsWith("dashboard_paused") ? "bg-red-100 text-red-700"
    : statusKey.startsWith("dashboard_") ? "bg-blue-100 text-blue-700"
    : statusKey.startsWith("intake_") ? "bg-purple-100 text-purple-700"
    : statusKey === "waitlist_invited" ? "bg-amber-100 text-amber-700"
    : "bg-gray-100 text-gray-600";
  const label =
    statusKey === "dashboard_live" ? "Live"
    : statusKey === "dashboard_paused" ? "Paused"
    : statusKey === "dashboard_awaiting" ? "Awaiting Activation"
    : statusKey === "intake_submitted" ? "Intake Received"
    : statusKey === "intake_in_progress" ? "Intake in Progress"
    : statusKey === "waitlist_invited" ? "Invited"
    : "Waitlisted";
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full ${tone}`}>
      {label}
    </span>
  );
}

function HostedBrandEditModal({ brand, open, onOpenChange, onSaved, onRemoved }) {
  const [brandName, setBrandName] = useState(brand?.brand_name || "");
  const [city, setCity] = useState(brand?.city || "");
  const [instagram, setInstagram] = useState(brand?.instagram || "");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  useEffect(() => {
    setBrandName(brand?.brand_name || "");
    setCity(brand?.city || "");
    setInstagram(brand?.instagram || "");
  }, [brand?.brand_id]);

  const token = localStorage.getItem("session_token");

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/me/hosted-brands/${brand.brand_id}`,
        { brand_name: brandName, city, instagram },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Hosting details updated");
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const performRemove = async () => {
    setRemoving(true);
    try {
      await axios.delete(`${API}/me/hosted-brands/${brand.brand_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Removed from your Hosting section");
      setConfirmRemoveOpen(false);
      onRemoved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
    } finally {
      setRemoving(false);
    }
  };

  if (!brand) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="hosted-brand-edit-modal">
        <DialogHeader>
          <DialogTitle>Edit Hosting details</DialogTitle>
          <DialogDescription>
            This is what shows in your /me Hosting section while you wait for {(brand.categories || []).join(" / ") || "your category"} to launch.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Brand name</label>
            <Input value={brandName} onChange={(e) => setBrandName(e.target.value)} data-testid="hosted-brand-edit-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">City</label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="hosted-brand-edit-city" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Instagram</label>
            <Input value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="@yourbrand" data-testid="hosted-brand-edit-instagram" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-4">
          <Button
            variant="ghost"
            onClick={() => setConfirmRemoveOpen(true)}
            disabled={removing}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="hosted-brand-remove-btn"
          >
            {removing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Remove
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="hosted-brand-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !brandName.trim()} data-testid="hosted-brand-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
      <ConfirmDeleteDialog
        open={confirmRemoveOpen}
        onOpenChange={setConfirmRemoveOpen}
        title="Remove this brand from Hosting?"
        description={`This removes "${brand?.brand_name}" from your /me Hosting section. Your waitlist entry stays intact - admin can still reach out.`}
        confirmLabel="Remove"
        loading={removing}
        onConfirm={performRemove}
      />
    </Dialog>
  );
}

export default function HostedBrandsSection() {
  const [brands, setBrands] = useState(null);
  const [editing, setEditing] = useState(null);

  const fetchBrands = useCallback(async () => {
    const token = localStorage.getItem("session_token");
    if (!token) {
      setBrands([]);
      return;
    }
    try {
      const r = await axios.get(`${API}/me/hosted-brands`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBrands(r.data.hosted_brands || []);
    } catch (err) {
      // 401 / 403 → just hide; the user might not be authenticated
      setBrands([]);
    }
  }, []);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  if (brands === null) {
    return (
      <div className="py-4 flex items-center justify-center text-gray-400 text-xs" data-testid="hosted-brands-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> Loading your hosting…
      </div>
    );
  }
  if (brands.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="hosted-brands-section">
      {brands.map((b) => {
        const statusKey = resolveStatusKey(b);
        const copyFn = STATUS_COPY[statusKey] || STATUS_COPY.waitlist_joined;
        return (
          <div
            key={b.brand_id}
            className="rounded-xl border border-gray-200 bg-white p-3 flex items-start gap-3"
            data-testid={`hosted-brand-card-${b.brand_id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-semibold text-sm text-gray-900 truncate" data-testid={`hosted-brand-name-${b.brand_id}`}>
                  {b.brand_name}
                </p>
                <StatusBadge statusKey={statusKey} />
              </div>
              <p className="text-xs text-gray-600 mb-1.5" data-testid={`hosted-brand-status-${b.brand_id}`}>
                {copyFn(b)}
              </p>
              <div className="flex items-center gap-3 text-[10px] text-gray-400">
                {b.city && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {b.city}
                  </span>
                )}
                {(b.categories || []).length > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <Tag className="w-3 h-3" /> {b.categories.join(" / ")}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Hourglass className="w-3 h-3" /> {b.waitlist_status}
                </span>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
                  data-testid={`hosted-brand-menu-${b.brand_id}`}
                  aria-label="Brand options"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setEditing(b)} data-testid={`hosted-brand-edit-${b.brand_id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-2" /> Edit details
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setEditing(b)}
                  className="text-red-600"
                  data-testid={`hosted-brand-remove-action-${b.brand_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Remove
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      })}
      <HostedBrandEditModal
        brand={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={fetchBrands}
        onRemoved={fetchBrands}
      />
    </div>
  );
}
